import { useKeycloak } from "@/app/hook/use-keycloak";
import { useLocalization } from "@/app/hook/use-localization";
import { useNotifications } from "@/app/hook/use-notifications";
import {
  AnnouncementTypeCodes,
  NotificationAnnouncementItemType,
} from "@/schemas/index";
import { useNotificationsService } from "@/services/index";
import { useCallback, useEffect, useMemo, useState } from "react";

export const getAlertType = (
  typeCode?: number,
): "info" | "warning" | "success" | "error" => {
  switch (typeCode) {
    case AnnouncementTypeCodes.New:
      return "success";
    case AnnouncementTypeCodes.Beta:
    case AnnouncementTypeCodes.Deploy:
      return "warning";
    case AnnouncementTypeCodes.Deprecate:
      return "error";
    default:
      return "info";
  }
};

export function useNotificationsHook() {
  const service = useNotificationsService();
  const notifications = useNotifications();
  const localization = useLocalization();
  const { authorize } = useKeycloak();
  const [announcements, setAnnouncements] = useState<
    NotificationAnnouncementItemType[]
  >([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsLoading(true);
    service
      .fetchAnnouncements()
      .then(setAnnouncements)
      .catch((e) => notifications.show(e as Error))
      .finally(() => setIsLoading(false));
  }, []);

  const visibleAnnouncements = useMemo(
    () => announcements.filter((a) => !dismissedIds.has(a.Id)),
    [announcements, dismissedIds],
  );

  const total = visibleAnnouncements.length;

  const goNext = useCallback(
    () => setCurrentIndex((i) => Math.min(i + 1, total - 1)),
    [total],
  );

  const goPrev = useCallback(
    () => setCurrentIndex((i) => Math.max(i - 1, 0)),
    [],
  );

  const dismiss = useCallback(
    (id: string) => {
      if (!authorize) {
        setDismissedIds((prev) => new Set(prev).add(id));
        setCurrentIndex((i) => Math.max(i - 1, 0));
        return;
      }

      service
        .deleteAnnouncement(id)
        .then(() => {
          setDismissedIds((prev) => new Set(prev).add(id));
          setCurrentIndex((i) => Math.max(i - 1, 0));
        })
        .catch((e) => notifications.show(e as Error));
    },
    [authorize, notifications, service],
  );

  return useMemo(
    () => ({
      announcements: visibleAnnouncements,
      isLoading,
      currentIndex,
      goNext,
      goPrev,
      dismiss,
      total,
      localization,
    }),
    [
      visibleAnnouncements,
      isLoading,
      currentIndex,
      goNext,
      goPrev,
      dismiss,
      total,
      localization,
    ],
  );
}
